// ============================================================
// Where each part of a marker goes on screen, and what it says
// ============================================================
// Pure. BattlefieldTokens projects each token's foot and body; everything
// else about the marker's shape, size, heading handle and tooltip is
// worked out here, per frame, from props - never stored on the Token.

import { pointsAttr } from '../../engine/display/svgPath';
import type { Camera, ScreenPoint } from '../../engine/battlefield/projection';
import { project, SCENE_VIEW } from '../../engine/battlefield/projection';
import type { Vec3 } from '../../engine/battlefield/types';
import type { Token } from '../../engine/battlefield/tokens';
import { footShape, bodyShape } from '../../engine/battlefield/tokenShape';
import type { ShapeKind } from '../../engine/battlefield/tokenShape';
import { headingPoint } from '../../engine/battlefield/facing';
import type { MarkerStatus } from '../../engine/battlefield/markerStatus';
import { healthBandLabel } from '../../engine/healthRamp';
import { decodeCond } from '../../engine/conditions';

/**
 * No footprint is ever smaller than this on screen.
 *
 * A token is something you GRAB. Below a few pixels it stops being a
 * target for a pointer however correct its projected size is, so the
 * geometry is allowed to be wrong in this one direction.
 */
const MIN_HIT_RADIUS = 5;

/** One condition chip, in viewBox units. Fixed, so it survives zooming out. */
export const CHIP = 14;
export const CHIP_GAP = 3;

/**
 * How far outside the FOOTPRINT the ammo ring sits.
 *
 * Further out than the wound ring's `GAUGE_GAP`, so the two never touch
 * and a glance tells them apart by where they are rather than by what
 * colour they happen to be - which matters, because both run the same
 * green-to-red ramp.
 */
export const AMMO_GAP = 7;

/**
 * How far outside the FOOTPRINT the heading arrow sits.
 *
 * Beyond the ammo wedge, on a ring of its own. The two gauges measure
 * turn CLOCKWISE FROM SCREEN-UP by deliberate policy (`gauge.ts`), and a
 * heading is a WORLD direction - putting them on the same circle would
 * be two angular meanings in one place, visibly disagreeing the moment
 * anybody orbits the map (v8.53.0).
 */
const HEADING_GAP = 12;

/**
 * The magazine is a QUARTER TURN at the bottom, not a whole ring.
 *
 * A second full circle would read as a second wound gauge. A quarter
 * cannot be mistaken for one, and the bottom is the only side of the
 * footprint nothing else has claimed: the name sits right of the body,
 * a horde's magnitude at the ground centre, and the `⚠` note below -
 * which moves down by `AMMO_GAP` to clear this.
 *
 * Turn runs clockwise from twelve o'clock, so 0.375 is below-RIGHT and
 * the wedge is traversed right to left. It is anchored at its start and
 * grows from there, exactly as the wound ring grows from the top.
 */
export const AMMO_FROM = 0.375;
export const AMMO_SPAN = 0.25;

/**
 * Below this footprint radius the number is DROPPED rather than drawn
 * illegibly - a figure over a three-unit dot is a blur.
 *
 * The rings have no such threshold: they are strokes, and every stroked
 * thing in this scene carries `vector-effect: non-scaling-stroke`, so
 * they hold their width in real pixels however far out you zoom.
 */
export const NUMBERS_MIN_RX = 8;

export interface ScreenXY {
  x: number;
  y: number;
}

/** A small filled triangle at `at`, pointing along `deg` (screen space). */
export function arrowHead(at: ScreenXY, deg: number): string {
  const rad = (deg * Math.PI) / 180;
  const p = (d: number, side: number) => ({
    x: at.x + Math.cos(rad) * d - Math.sin(rad) * side,
    y: at.y + Math.sin(rad) * d + Math.cos(rad) * side,
  });
  const tip = p(5, 0);
  const l = p(-3, 4);
  const r = p(-3, -4);
  const f = (n: number) => Number(n.toFixed(2));
  return `M ${f(tip.x)} ${f(tip.y)} L ${f(l.x)} ${f(l.y)} L ${f(r.x)} ${f(r.y)} Z`;
}

/** A chevron pointing down at (x, y): the actor's "this is you". */
export function chevron(x: number, y: number, w: number): string {
  return `M${(x - w).toFixed(1)},${(y - w * 0.8).toFixed(1)} L${x.toFixed(1)},${y.toFixed(1)} L${(x + w).toFixed(1)},${(y - w * 0.8).toFixed(1)}`;
}

/**
 * The arc of a ring that faces a point (plan design-gaps, T6: the target's cover, bright toward the shooter): the
 * ellipse (cx, cy, rx, ry) from `spread` degrees either side of the bearing to `toward`, as polyline points. Screen
 * units.
 */
export function arcToward(
  ring: { cx: number; cy: number; rx: number; ry: number },
  toward: { x: number; y: number },
  spread = 50,
): string {
  const facing = Math.atan2((toward.y - ring.cy) / ring.ry, (toward.x - ring.cx) / ring.rx);
  const half = (spread * Math.PI) / 180;
  return pointsAttr(
    Array.from({ length: 13 }, (_, i) => {
      const a = facing - half + (2 * half * i) / 12;
      return { x: ring.cx + ring.rx * Math.cos(a), y: ring.cy + ring.ry * Math.sin(a) };
    }),
  );
}

/** Four reticle corners round (x, y) at radius r: the target's lock. */
export function reticle(x: number, y: number, r: number): string {
  const t = Math.max(3, r * 0.45);
  const c = (sx: number, sy: number) =>
    `M${(x + sx * r).toFixed(1)},${(y + sy * (r - t)).toFixed(1)} ` +
    `L${(x + sx * r).toFixed(1)},${(y + sy * r).toFixed(1)} ` +
    `L${(x + sx * (r - t)).toFixed(1)},${(y + sy * r).toFixed(1)}`;
  return [c(-1, -1), c(1, -1), c(1, 1), c(-1, 1)].join(' ');
}

/** The per-marker records BattlefieldTokens is handed, keyed by token id. */
export interface MarkerLookups {
  bodyTypes: Record<string, ShapeKind>;
  scales: Record<string, number>;
  crowds: Record<string, boolean>;
  headings: Record<string, number>;
}

interface HeadingHandle {
  /** The stored heading, if any - `null` and `undefined` both mean unset. */
  heading: number | null | undefined;
  hasHeading: boolean;
  /** Where the arrow ends on screen; null when the marker has no handle. */
  headTo: ScreenXY | null;
  headDeg: number;
}

export interface MarkerGeometry extends HeadingHandle {
  rx: number;
  ry: number;
  kind: ShapeKind;
  crowd: boolean;
  bodyR: number;
  footD: string | null;
  bodyD: string | null;
}

/**
 * WHICH WAY THE HULL POINTS, placed by projecting a WORLD point and taking
 * the screen direction from the footprint - not by parameterising the
 * footprint's screen ellipse at the same angle, which is wrong at every
 * camera but straight down and wrong in a way that looks plausible
 * (v8.53.0).
 *
 * EVERY VEHICLE GETS THE HANDLE, turned or not: the only thing that can
 * set a heading is this handle, so drawing it only once one existed meant
 * no vehicle ever showed an arrow. An unset hull shows it dimmed, pointing
 * due east, and says so (v8.55.0).
 *
 * `!= null` catches BOTH undefined and null, and the difference is not
 * pedantry: Firestore omits an unwritten field, while a stored schema puts
 * null in the column. A strict `!== undefined` read every headingless
 * vehicle as headed. Zero is a REAL heading, so truthiness would be wrong
 * too; `heading` is a json column for the same reason.
 */
function headingHandleOf(token: Token, heading: number | null | undefined, foot: ScreenXY, rx: number, at: Place) {
  const out = headingPoint(token, heading ?? 0, Math.max(0.5, token.footprint));
  const p = at({ x: out.x, y: token.elevation, z: out.z });
  const dx = p.x - foot.x;
  const dy = p.y - foot.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    headDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
    headTo: { x: foot.x + (dx / len) * (rx + HEADING_GAP), y: foot.y + (dy / len) * (rx + HEADING_GAP) },
  };
}

/** Puts a world point on the screen: the map's projection, or a marker's own frame (markerFrame). */
type Place = (p: Vec3) => ScreenPoint;

/**
 * The marker's size, shape and heading handle on screen - in the frame `foot`, `body` and `at` share (the map's,
 * or the marker's own: markerFrame).
 *
 * The footprint is foreshortened by the pitch: SIN, not cos. Looking
 * straight down (pitch 90°) at a circle on the ground you see a full
 * circle; from a low angle a sliver. Cosine has it exactly backwards, and
 * collapsed every footprint to a 1.5-pixel line in the top-down view.
 *
 * The kind is DERIVED PER FRAME, never stored on the Token: body type is
 * live-editable while a token is written once, at deploy (v8.39.0).
 */
export function markerGeometry(
  token: Token,
  foot: ScreenXY,
  body: ScreenXY,
  camera: Camera,
  lookups: MarkerLookups,
  at: Place = (p) => project(p, camera, SCENE_VIEW),
): MarkerGeometry {
  const rx = Math.max(3, token.footprint * (lookups.scales[token.id] ?? 1) * camera.zoom);
  const ry = Math.max(MIN_HIT_RADIUS, rx * Math.sin((camera.pitch * Math.PI) / 180));
  const kind = lookups.bodyTypes[token.id] ?? 'humanoid';
  const bodyR = Math.max(4, rx * 0.75);
  const heading = lookups.headings[token.id];
  // A hull turns, and so does an Object (2026-10-26): its box, when it counts as terrain, turns with its heading.
  const turns = kind === 'vehicle' || kind === 'object';
  const handle = turns ? headingHandleOf(token, heading, foot, rx, at) : { headTo: null, headDeg: 0 };
  return {
    rx,
    ry,
    kind,
    crowd: !!lookups.crowds[token.id],
    bodyR,
    footD: footShape(kind, foot.x, foot.y, rx, ry),
    bodyD: bodyShape(kind, body.x, body.y, bodyR),
    heading,
    hasHeading: heading != null,
    ...handle,
  };
}

/** The wounds in words: exact for whoever may read them, a band otherwise. */
function woundsWords(status: MarkerStatus, showNumbers: boolean): string {
  if (!showNumbers) return healthBandLabel(status.shown, status.total);
  return `${status.shown}/${status.total}${status.isHorde ? ' mag' : ''}`;
}

/**
 * The status, in words, for the marker's tooltip.
 *
 * The chips are `aria-hidden` and the ring has no text at all, so this is
 * the only place a screen reader - or anyone hovering - is told what the
 * marks mean, including the conditions that overflowed.
 *
 * THE TOOLTIP LEAKED AT EVERY LEVEL once: `status.numbers` is gated on the
 * 'full' marker setting, but this string never was - so a viewer on
 * 'minimal' read the exact wounds by hovering. Hence `showNumbers`.
 */
function statusTitle(status: MarkerStatus | undefined, showNumbers = true): string {
  if (!status) return '';
  const parts: string[] = [];
  if (status.total > 0) parts.push(woundsWords(status, showNumbers));
  for (const code of status.chips) parts.push(decodeCond(code) || code);
  if (status.overflow > 0) parts.push(`+${status.overflow} more`);
  if (status.ammo) parts.push(`${status.ammo.current}/${status.ammo.max} loaded`);
  return parts.length ? ` - ${parts.join(', ')}` : '';
}

/** One line of the hover card: a label, a value at its right, `bad` in the warning colour (a condition). */
export interface CardLine {
  label: string;
  value?: string;
  bad?: boolean;
}

/**
 * THE HOVER CARD on the display (plan design-gaps, T3; the focus page): the hovered figure's name, its wounds - as
 * exact as the tooltip may say (`showNumbers`) - its conditions (or "no conditions"), and how far it is from whoever
 * acts. With no status to tell (markers set to say nothing), the name and the distance alone.
 */
export function hoverCardOf(
  name: string,
  status: MarkerStatus | undefined,
  showNumbers: boolean,
  from: { name: string; metres: number } | null,
): { title: string; lines: CardLine[] } {
  const fromLine = from ? [{ label: `From ${from.name}`, value: `${from.metres.toFixed(1)} m` }] : [];
  if (!status) return { title: name, lines: fromLine };
  const wounds = status.total > 0 ? [{ label: 'Wounds', value: woundsWords(status, showNumbers) }] : [];
  const conditions: CardLine[] = status.chips.map((code) => ({ label: decodeCond(code) || code, bad: true }));
  if (status.overflow > 0) conditions.push({ label: `+${status.overflow} more`, bad: true });
  return {
    title: name,
    lines: [...wounds, ...(conditions.length ? conditions : [{ label: 'no conditions' }]), ...fromLine],
  };
}

/**
 * The body's tooltip: name, height off the ground, status, and what they
 * are standing in. The chips are aria-hidden, so this is where the
 * conditions are actually readable - and the only place the ones that did
 * not fit are named at all.
 */
export function markerTitle(
  token: Token,
  name: string | undefined,
  status: MarkerStatus | undefined,
  showNumbers: boolean,
  overlap: string | undefined,
): string {
  return (
    `${name || token.id}` +
    `${token.elevation !== 0 ? ` - ${token.elevation.toFixed(1)} m` : ''}` +
    `${statusTitle(status, showNumbers)}` +
    `${overlap ? ` - ${overlap}` : ''}`
  );
}
